import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useEnv } from '../config/env';
import { catalogFeed, useOutcomeMeta } from '../catalog/useOutcomeMeta';
import { outcomeMid, useAllMids } from '../hl/mids';
import { useHlEnv } from '../hl/react';
import { coin as outcomeCoin, type Side } from '../hl/outcomes';
import type { OutcomeSpec } from '../hl/types';
import OrderBook, { type LevelClick } from './OrderBook';
import TradesTape from './TradesTape';
import TouchPanel, { TouchBadge } from './TouchPanel';
import { setTicketPrefill } from './ticketPrefill';
import { pxToCents } from './format';
import OrderTicket from '../ticket/OrderTicket';
import MarketOpenOrders from '../ticket/MarketOpenOrders';
import OutOfVenue from '../venue/OutOfVenue';
import { inVenue } from '../venue/scope';
import { useVenueScope } from '../venue/useVenueScope';
import type { OutcomeDescription } from '../describe';
import { useDescriber } from '../describe/useDescribe';
import { CHIP, CategoryChip, Countdown, DetailsTable, LocalTime, RawDescription, VenueChip } from '../describe/ui';
import { useViewedAccount } from '../wallet/context';
import { useSettledOutcome, useSettlements } from '../settlement/useSettlements';
import { SettlementBanner } from '../settlement/ui';
import { whenLabel } from '../describe/when';
import { networkWord } from '../i18n/common';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';
import VolumePanel from '../volume/VolumePanel';
import { useMediaQuery } from '../catalog/useMediaQuery';

type Tab = 'book' | 'trades';

/**
 * How the market is laid out (#1953). `page`: the /market/:id route. `wide`: inline on Markets, across the whole list
 * (under a row of tiles); same columns as the page, shorter header. `narrow`: inline in a column about 30-40rem wide
 * (beside the compact list, or under a compact row on a phone), so book and trades stay behind tabs at every width.
 */
export type MarketVariant = 'page' | 'wide' | 'narrow';

export function NotFound({ raw, envName }: { raw: string; envName: string }) {
  const t = useT();
  return (
    <section className="flex flex-col items-center gap-3 py-16 text-center" data-testid="outcome-not-found">
      <h1 className="text-lg font-semibold">{t('market.not_found.title', { id: raw })}</h1>
      <p className="max-w-md text-sm text-muted">
        {t('market.not_found.body', { network: networkWord(envName) })}
      </p>
      <Link to="/" className="text-sm text-accent hover:underline">
        {t('common.back_to_markets')}
      </Link>
    </section>
  );
}

/**
 * #788: an outcome that is no longer in outcomeMeta may have settled. Ask /info settledOutcome and show its result
 * (plus the viewed wallet's own payout) instead of "not found".
 */
function SettledMarket({ id, raw, envName, Heading }: { id: number; raw: string; envName: string; Heading: 'h1' | 'h2' }) {
  const settled = useSettledOutcome(id, true, catalogFeed(useQueryClient(), useHlEnv()).settledAt(id));
  const { address } = useViewedAccount();
  const mine = useSettlements(address).results.filter((r) => r.outcome === id);
  const t = useT();
  if (settled.isPending || settled.waiting) {
    return (
      <section className="space-y-4">
        <Heading className="text-xl font-semibold sm:text-2xl">{t('market.outcome_heading', { id: String(id) })}</Heading>
        <div className="card p-6 text-sm text-muted">{t('market.loading')}</div>
      </section>
    );
  }
  if (!settled.result) return <NotFound raw={raw} envName={envName} />;
  const m = settled.result;
  return (
    <section className="space-y-4" data-testid="settled-market">
      <div className="card space-y-1 p-4">
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
          <span className="num">#{id}</span>
          <span>{t('market.settled')}</span>
        </div>
        <Heading className="break-words text-xl font-semibold leading-snug sm:text-2xl" data-testid="outcome-title" translate="no">
          {m.title}
        </Heading>
        <p className="text-xs text-muted">
          <Msg
            k="market.sides"
            args={{
              a: (
                <span className="text-yes" translate="no">
                  {m.sideLabels[0]}
                </span>
              ),
              b: (
                <span className="text-no" translate="no">
                  {m.sideLabels[1]}
                </span>
              ),
            }}
          />
        </p>
      </div>
      <SettlementBanner market={m} mine={mine} />
      <p className="text-sm">
        <Link to="/" className="text-accent hover:underline">
          {t('common.back_to_markets')}
        </Link>
      </p>
    </section>
  );
}

/** Inline only (#2242): the full-page link and, while the ticket is under the book, the panel's close button. */
interface HeaderActions {
  pageHref?: string;
  onClose?: () => void;
}

function OutcomeHeader({
  meta,
  desc,
  inline,
  actions,
}: {
  meta: OutcomeSpec;
  desc: OutcomeDescription;
  inline: boolean;
  actions?: HeaderActions;
}) {
  const t = useT();
  const [a, b] = desc.sideLabels;
  const Heading = inline ? 'h2' : 'h1';
  return (
    <div className={inline ? 'space-y-2' : 'card space-y-3 p-4'}>
      <div className="space-y-1">
        {/* Inline (#2242) every line of the header has a fixed height (one line of chips, two of title, one of subtitle,
            one of sides and time), so the book under it stays put from one outcome to the next. */}
        <div
          className={`flex items-center gap-1.5 text-xs text-muted ${inline ? 'min-h-5 flex-nowrap whitespace-nowrap' : 'flex-wrap'}`}
        >
          <CategoryChip category={desc.category} />
          {desc.touch ? <TouchBadge /> : null}
          <span className={`num ${CHIP}`} data-testid="outcome-id">#{meta.outcome}</span>
          {meta.venue ? <VenueChip venue={meta.venue} /> : null}
          {desc.question !== undefined && desc.questionTitle ? (
            <Link
              to={`/?q=${encodeURIComponent(desc.questionTitle)}`}
              className={`text-accent hover:underline ${inline ? 'min-w-0 truncate' : ''}`}
              title={inline ? t('market.part_of', { question: desc.questionTitle }) : undefined}
              data-testid="question-link"
            >
              {t('market.part_of', { question: desc.questionTitle })}
            </Link>
          ) : null}
          {actions?.pageHref !== undefined ? (
            <Link
              to={actions.pageHref}
              className="ms-auto shrink-0 py-0.5 text-accent hover:underline max-sm:py-3"
              data-testid="market-panel-page"
            >
              {t('catalog.panel_full_page')}
            </Link>
          ) : null}
          {actions?.onClose ? (
            <button
              type="button"
              onClick={actions.onClose}
              aria-label={t('common.close')}
              title={t('common.close')}
              data-testid="market-panel-close"
              className={`${actions.pageHref === undefined ? 'ms-auto' : ''} -my-1 -me-1 shrink-0 rounded-md px-2 py-1 text-sm text-muted hover:bg-surface-2 hover:text-fg max-sm:min-h-10 max-sm:min-w-10`}
            >
              ✕
            </button>
          ) : null}
        </div>
        <Heading
          className={`break-words font-semibold leading-snug ${inline ? 'line-clamp-2 text-base sm:h-11' : 'text-xl sm:text-2xl'}`}
          data-testid="outcome-title"
          title={inline ? desc.title : undefined}
          translate="no"
        >
          {desc.title}
        </Heading>
        {inline ? (
          <p className="line-clamp-1 h-4 break-words text-xs text-muted" translate="no" title={desc.subtitle || undefined}>
            {desc.subtitle || ' '}
          </p>
        ) : desc.subtitle ? (
          <p className="line-clamp-3 break-words text-sm text-muted" translate="no">
            {desc.subtitle}
          </p>
        ) : null}
        <p
          className={`flex items-center gap-x-3 gap-y-1 text-xs text-muted ${inline ? 'h-4 flex-nowrap overflow-hidden whitespace-nowrap' : 'flex-wrap'}`}
        >
          <span className="shrink-0">
            <Msg
              k="market.sides"
              args={{
                a: <span className="text-yes" translate="no">{a}</span>,
                b: <span className="text-no" translate="no">{b}</span>,
              }}
            />
          </span>
          {desc.resolvesAt ? (
            <span className={inline ? 'min-w-0 truncate' : undefined}>
              {whenLabel(desc.resolvesLabel)} <LocalTime date={desc.resolvesAt} /> ·{' '}
              <Countdown date={desc.resolvesAt} label={desc.resolvesLabel} />
              {desc.touch ? <span className="text-warn"> · {t('touch.early')}</span> : null}
            </span>
          ) : null}
        </p>
        {desc.resolvesAt && desc.resolvesLabel !== 'Starts' && desc.resolvesAt.getTime() < Date.now() ? (
          <p
            className={`text-xs text-warn ${inline ? 'truncate' : ''}`}
            title={inline ? t('market.awaiting') : undefined}
            data-testid="awaiting-result"
          >
            {t('market.awaiting')}
          </p>
        ) : null}
      </div>
      {/* Inline, the rule details and raw description stay on the full page: the panel links to it. */}
      {!inline && desc.details.length > 0 ? (
        <details className="text-xs" data-testid="market-details">
          <summary className="cursor-pointer select-none text-muted hover:text-fg">{t('market.details')}</summary>
          <div className="mt-2">
            <DetailsTable rows={desc.details} />
          </div>
        </details>
      ) : null}
      {inline ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <RawDescription name={meta.name} description={meta.description} />
          <Link to="/learn" className="text-xs text-accent hover:underline" data-testid="market-learn-link">
            {t('ticket.learn_link')}
          </Link>
        </div>
      )}
    </div>
  );
}

export interface MarketViewProps {
  id: number;
  /** The id as it was asked for, for the "not found" text. */
  raw: string;
  side: Side;
  onSide: (s: Side) => void;
  variant?: MarketVariant;
  /** Inline (#2242): the full page of this outcome (shown at the end of the header's chip row). */
  pageHref?: string;
  /** Inline: closes the panel (the ✕ after the ticket's "How does this work?", or in the chip row when stacked). */
  onClose?: () => void;
}

/** Where the inline ticket stands beside the book rather than under it (the section grids below). */
const TICKET_BESIDE: Record<Exclude<MarketVariant, 'page'>, string> = {
  narrow: '(min-width: 1280px)',
  wide: '(min-width: 1024px)',
};

/**
 * One outcome: book, trades, order entry and everything that can stand in for them (loading, settled, out of venue, not
 * found). The /market/:id page and the inline panel on Markets (#1953) are both this component, and only one is ever
 * mounted, so it is the one instrument on the socket (#1809): `l2Book` and `trades` of the coin of `side`, let go at once
 * on a flip or on leaving.
 */
export default function MarketView({ id, raw, side, onSide, variant = 'page', pageHref, onClose }: MarketViewProps) {
  const t = useT();
  const { env } = useEnv();
  const [tab, setTab] = useState<Tab>('book');
  const metaQ = useOutcomeMeta();
  const describe = useDescriber(metaQ.data);
  const { venue: scopeVenue } = useVenueScope();
  const { mids } = useAllMids();
  const inline = variant !== 'page';
  const narrow = variant === 'narrow';
  const ticketBeside = useMediaQuery(TICKET_BESIDE[variant === 'page' ? 'wide' : variant]);
  const Heading = inline ? 'h2' : 'h1';

  if (metaQ.isPending) {
    return (
      <section className="space-y-4">
        <Heading className="text-xl font-semibold sm:text-2xl">{t('market.outcome_heading', { id: String(id) })}</Heading>
        <div className="card p-6 text-sm text-muted">{t('market.loading')}</div>
      </section>
    );
  }
  if (metaQ.isError) {
    return (
      <section className="space-y-4">
        <Heading className="text-xl font-semibold sm:text-2xl">{t('market.outcome_heading', { id: String(id) })}</Heading>
        <div className="card flex items-center justify-between gap-3 p-6 text-sm">
          <span className="text-danger">{t('market.load_error', { error: String(metaQ.error) })}</span>
          <button type="button" className="text-accent hover:underline" onClick={() => void metaQ.refetch()}>
            {t('common.retry')}
          </button>
        </div>
      </section>
    );
  }

  const meta = metaQ.data.outcomes.find((o) => o.outcome === id);
  if (!meta) return <SettledMarket id={id} raw={raw} envName={env.name} Heading={Heading} />;
  // #689: outcomes outside the venue scope get a notice page, never a book or an order ticket.
  if (!inVenue(meta.venue, scopeVenue)) return <OutOfVenue outcome={id} outcomeVenue={meta.venue} envName={env.name} />;

  const desc = describe.outcome(meta);
  const sideNames = desc.sideLabels;
  const coin = outcomeCoin(id, side);

  const midOf = (s: Side) => outcomeMid(mids, id, s);

  const onLevelClick = (c: LevelClick) =>
    setTicketPrefill({ outcome: id, side, px: c.px, isBuy: c.bookSide === 'ask' });

  const tabBtn = (tb: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === tb}
      onClick={() => setTab(tb)}
      className={`min-h-10 flex-1 rounded-md px-3 py-1.5 text-sm font-medium sm:min-h-0 ${
        tab === tb ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'
      }`}
    >
      {label}
    </button>
  );
  // Page: behind tabs on a narrow screen, side by side from lg.
  const tabCls = (tb: Tab) => (tab === tb ? '' : 'hidden lg:block');

  const sides = (
    <div role="radiogroup" aria-label={t('market.side_group')} data-tour="market-sides" className="flex max-w-sm rounded-lg bg-surface p-1">
      {([0, 1] as const).map((s) => {
        const active = s === side;
        const activeCls = s === 0 ? 'bg-yes/20 text-yes' : 'bg-no/20 text-no';
        return (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onSide(s)}
            className={`flex-1 truncate rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              active ? activeCls : 'text-muted hover:text-fg'
            }`}
          >
            <span translate="no">{sideNames[s]}</span>
            {midOf(s) !== undefined && (
              <span className="num ms-1.5 font-normal opacity-80" data-testid={`side-mid-${s}`}>
                {pxToCents(midOf(s)!)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const tabs = (
    <div role="tablist" aria-label={t('market.tabs')} data-tour="market-tabs" className={`flex rounded-lg bg-surface p-1 ${narrow ? '' : 'lg:hidden'}`}>
      {tabBtn('book', t('market.tab_book'))}
      {tabBtn('trades', t('market.tab_trades'))}
    </div>
  );

  const bookTitle = (
    <h2 className="mb-2 px-3 text-sm font-semibold">
      <Msg
        k="market.book_title"
        args={{ side: <span className={side === 0 ? 'text-yes' : 'text-no'} translate="no">{sideNames[side]}</span> }}
      />
    </h2>
  );
  const tradesTitle = (
    <h2 className="mb-2 px-3 text-sm font-semibold">
      <Msg
        k="market.trades_title"
        args={{ side: <span className={side === 0 ? 'text-yes' : 'text-no'} translate="no">{sideNames[side]}</span> }}
      />
    </h2>
  );
  const book = <OrderBook coin={coin} sideLabel={sideNames[side]} onLevelClick={onLevelClick} />;
  const trades = <TradesTape coin={coin} />;

  if (!inline) {
    return (
      <section className="space-y-4">
        <OutcomeHeader meta={meta} desc={desc} inline={false} />
        {desc.touch ? <TouchPanel touch={desc.touch} venue={meta.venue} /> : null}
        {sides}
        {tabs}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_320px]">
          <div className={`card py-3 ${tabCls('book')}`} data-tour="market-book">
            {bookTitle}
            {book}
          </div>
          <div className={`card py-3 ${tabCls('trades')}`} data-tour="market-trades">
            {tradesTitle}
            {trades}
          </div>
          <div>
            <OrderTicket outcome={id} sideNames={sideNames} side={side} onSideChange={onSide} />
            <MarketOpenOrders outcome={id} sideNames={sideNames} />
          </div>
        </div>
        <VolumePanel outcome={id} />
      </section>
    );
  }

  // Inline on Markets (#2242): the order ticket is a column of its own from the top, beside the header, the Book/Trades
  // tabs and the book; its Yes / No pick the side (with their prices), so there is no second pair over the book. Stacked
  // on narrow screens. The panel keeps one size from outcome to outcome: the header lines have fixed heights, the book is
  // always BOOK_LEVELS rows a side, and the trades take the book's height (scrolling inside) whether behind a tab or
  // beside it.
  const bookCard = (
    <div className={`card py-3 ${tab === 'book' ? '' : narrow ? 'invisible' : 'invisible lg:visible'}`} data-tour="market-book">
      {bookTitle}
      {book}
    </div>
  );
  const tradesCard = (
    <div
      className={`card pointer-events-auto absolute inset-0 overflow-y-auto py-3 ${tab === 'trades' ? '' : narrow ? 'hidden' : 'hidden lg:block'}`}
      data-tour="market-trades"
    >
      {tradesTitle}
      {trades}
    </div>
  );
  return (
    <section
      className={`grid gap-3 ${narrow ? 'xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-4' : 'lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-4'}`}
      data-testid="market-view"
    >
      <div className="min-w-0 space-y-3">
        <OutcomeHeader meta={meta} desc={desc} inline actions={{ pageHref, onClose: ticketBeside ? undefined : onClose }} />
        {desc.touch ? <TouchPanel touch={desc.touch} venue={meta.venue} /> : null}
        {tabs}
        {narrow ? (
          <div className="relative">
            {bookCard}
            {tradesCard}
          </div>
        ) : (
          <div className="relative lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:gap-4">
            {bookCard}
            <div className="pointer-events-none absolute inset-0 lg:relative lg:inset-auto">{tradesCard}</div>
          </div>
        )}
      </div>
      <div>
        <OrderTicket
          outcome={id}
          sideNames={sideNames}
          side={side}
          onSideChange={onSide}
          onClose={ticketBeside ? onClose : undefined}
        />
        <MarketOpenOrders outcome={id} sideNames={sideNames} />
      </div>
    </section>
  );
}
